import type {
  OrderAddon,
  OrderAddonPerRequestQuantities,
  OrderAddonQuantities,
  OrderAddonTrackerQuantities,
} from '../types/ordersApiTypes';
import pricingData from '@/app/Pricing/pricing-data.json';
import { formatToman } from '@/app/Pricing/utils';
import { ui as pricingUi } from '@/app/Pricing/constants';

const mentoringPlanNames = Object.fromEntries(
  pricingData.addons.mentoring.plans.map((plan) => [plan.key, plan.name]),
) as Record<string, string>;

const ADDON_LABELS: Record<string, string> = {
  wallet: pricingData.addons.wallet.name,
  tracker: pricingData.addons.tracker.name,
  trcaker: pricingData.addons.tracker.name,
  mentoring_seo: 'طرح منتور سئو',
  mentoring_seo_360: mentoringPlanNames.seo_360,
};

export function getAddonLabel(name: string): string {
  return ADDON_LABELS[name] ?? name.replace(/_/g, ' ');
}

export function getNormalizedTariffName(tariffName?: string | null): string {
  return tariffName === 'بدون طرح اصلی' ? 'بدون طرح' : tariffName || '—';
}

export function getOrderAddonDisplayLabels(
  addonNames?: string[] | null,
): string[] {
  return addonNames?.map((name) => getAddonLabel(name.trim())).filter(Boolean) ?? [];
}

export function formatOrderTariffLabel(
  tariffName?: string | null,
  addonNames?: string[] | null,
): string {
  const normalizedTariff = getNormalizedTariffName(tariffName);
  const names = getOrderAddonDisplayLabels(addonNames);
  if (!names.length) return normalizedTariff;

  return `${normalizedTariff} (${names.join('، ')})`;
}

export function isTrackerQuantities(
  quantities: OrderAddonQuantities,
): quantities is OrderAddonTrackerQuantities {
  return (
    quantities !== null &&
    typeof quantities === 'object' &&
    'keywords' in quantities &&
    'depth' in quantities
  );
}

export type AddonDetailRow = {
  label: string;
  value: string;
};

export function getAddonDetailRows(addon: OrderAddon): AddonDetailRow[] {
  if (addon.name === 'wallet') {
    return [
      {
        label: 'مبلغ شارژ',
        value: formatOrderAmount(addon.amount_paid),
      },
    ];
  }

  const { quantities } = addon;

  if (quantities === null || quantities === undefined) {
    return [];
  }

  if (isTrackerQuantities(quantities)) {
    return [
      { label: 'تعداد کلمات', value: String(quantities.keywords) },
      { label: 'عمق بررسی', value: `صفحه ${quantities.depth}` },
      {
        label: 'بروزرسانی هفتگی',
        value: `${quantities.weekly_updates} بار`,
      },
    ];
  }

  return Object.entries(quantities as OrderAddonPerRequestQuantities | Record<string, number>)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => ({
      label: formatQuantityKey(key),
      value: String(value),
    }));
}

function formatQuantityKey(key: string): string {
  const labels: Record<string, string> = {
    keywords: 'تعداد کلمات',
    depth: 'عمق بررسی',
    weekly_updates: 'بروزرسانی هفتگی',
  };

  return labels[key] ?? key.replace(/_/g, ' ');
}

export function formatOrderAmount(amount: number): string {
  if (amount === 0) return pricingUi.common.free;
  return formatToman(amount);
}

export function getAddonAccentColor(name: string): string {
  switch (name) {
    case 'wallet':
      return '#5bb7ff';
    case 'tracker':
    case 'trcaker':
      return '#7c6cff';
    case 'mentoring_seo':
    case 'mentoring_seo_360':
      return '#f5a623';
    default:
      return 'var(--falcon-primary)';
  }
}
